import { useCallback, useEffect, useState } from 'react'
import {
  Bell,
  Save,
  RefreshCw,
  Play,
  AlertCircle,
  CheckCircle,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import type { NotificationRule } from '../../types'

const SERVICE_TYPES: { key: string; labelKey: string }[] = [
  { key: 'esignature_reminder', labelKey: 'notifRules.serviceEsignature' },
  { key: 'invoice_reminder', labelKey: 'notifRules.serviceInvoice' },
]

function RuleCard({
  rule,
  onSave,
  saving,
}: {
  rule: NotificationRule
  onSave: (updated: Partial<NotificationRule>) => void
  saving: boolean
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<NotificationRule>({ ...rule })

  useEffect(() => {
    setDraft({ ...rule })
  }, [rule])

  const serviceLabel = SERVICE_TYPES.find((s) => s.key === rule.service_type)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          {serviceLabel ? t(serviceLabel.labelKey) : rule.service_type}
        </h3>
        <button
          onClick={() => setDraft((d) => ({ ...d, is_enabled: !d.is_enabled }))}
          title={draft.is_enabled ? t('notifRules.disable') : t('notifRules.enable')}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          {draft.is_enabled ? (
            <ToggleRight size={28} className="text-green-500" />
          ) : (
            <ToggleLeft size={28} className="text-gray-400" />
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <label className="block">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
            {t('notifRules.initialDelay')}
          </span>
          <input
            type="number"
            min={0}
            value={draft.initial_delay_days}
            onChange={(e) => setDraft((d) => ({ ...d, initial_delay_days: +e.target.value }))}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
          />
          <span className="text-xs text-gray-400 mt-0.5 block">{t('notifRules.days')}</span>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
            {t('notifRules.reminderInterval')}
          </span>
          <input
            type="number"
            min={1}
            value={draft.reminder_interval_days}
            onChange={(e) => setDraft((d) => ({ ...d, reminder_interval_days: +e.target.value }))}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
          />
          <span className="text-xs text-gray-400 mt-0.5 block">{t('notifRules.days')}</span>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
            {t('notifRules.maxReminders')}
          </span>
          <input
            type="number"
            min={0}
            value={draft.max_reminders}
            onChange={(e) => setDraft((d) => ({ ...d, max_reminders: +e.target.value }))}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
          />
          <span className="text-xs text-gray-400 mt-0.5 block">
            {t('notifRules.maxRemindersHint')}
          </span>
        </label>
      </div>

      <button
        disabled={saving}
        onClick={() => onSave(draft)}
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
      >
        <Save size={15} />
        {saving ? t('common.saving') : t('common.save')}
      </button>
    </div>
  )
}

export default function AdminNotificationRules() {
  const { t } = useTranslation()

  const isSuperAdmin = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? '{}').role === 'super_admin'
    } catch {
      return false
    }
  })()

  const [erpId, setErpId] = useState<number | null>(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') ?? '{}')
      return u.erp_id ? +u.erp_id : null
    } catch {
      return null
    }
  })

  const [erps, setErps] = useState<{ id: number; erp_name: string }[]>([])
  const [rules, setRules] = useState<NotificationRule[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState<string | null>(null)
  const [triggerLoading, setTriggerLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (isSuperAdmin) {
      api
        .getAdminERPs()
        .then((res) => setErps(res))
        .catch(() => {})
    }
  }, [isSuperAdmin])

  const loadRules = useCallback(
    async (id: number) => {
      setLoading(true)
      setError('')
      try {
        const data = await api.getNotificationRules(id)
        const merged = SERVICE_TYPES.map((st) => {
          const existing = data.find((r: NotificationRule) => r.service_type === st.key)
          return (
            existing ??
            ({
              service_type: st.key,
              is_enabled: true,
              initial_delay_days: 0,
              reminder_interval_days: 7,
              max_reminders: 3,
            } as NotificationRule)
          )
        })
        setRules(merged)
      } catch {
        setError(t('notifRules.loadError'))
      } finally {
        setLoading(false)
      }
    },
    [t]
  )

  useEffect(() => {
    if (erpId) loadRules(erpId)
  }, [erpId, loadRules])

  const handleSave = async (serviceType: string, updated: Partial<NotificationRule>) => {
    if (!erpId) return
    setSaving(serviceType)
    setSuccess('')
    setError('')
    try {
      const saved = await api.upsertNotificationRule(erpId, serviceType, updated)
      setRules((prev) => prev.map((r) => (r.service_type === serviceType ? { ...r, ...saved } : r)))
      setSuccess(t('notifRules.savedOk'))
      setTimeout(() => setSuccess(''), 3000)
    } catch {
      setError(t('notifRules.saveError'))
    } finally {
      setSaving(null)
    }
  }

  const handleTriggerReminders = async () => {
    setTriggerLoading(true)
    setError('')
    setSuccess('')
    try {
      const result = await api.triggerSignatureReminders()
      setSuccess(
        t('notifRules.triggerOk', {
          sent: result.results?.sent ?? 0,
          skipped: result.results?.skipped ?? 0,
          failed: result.results?.failed ?? 0,
        })
      )
      setTimeout(() => setSuccess(''), 5000)
    } catch {
      setError(t('notifRules.triggerError'))
    } finally {
      setTriggerLoading(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <div className="flex items-center gap-3 mb-6">
        <Bell className="text-blue-500" size={24} />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('notifRules.title')}
        </h1>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('notifRules.subtitle')}</p>

      {isSuperAdmin && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('notifRules.selectErp')}
          </label>
          <select
            value={erpId ?? ''}
            onChange={(e) => setErpId(e.target.value ? +e.target.value : null)}
            className="w-full max-w-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
          >
            <option value="">{t('notifRules.chooseErp')}</option>
            {erps.map((e) => (
              <option key={e.id} value={e.id}>
                {e.erp_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
          <CheckCircle size={16} /> {success}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={20} className="animate-spin text-blue-500 mr-2" />
          <span className="text-sm text-gray-500">{t('common.loading')}</span>
        </div>
      )}

      {!loading && rules.length > 0 && (
        <div className="space-y-4">
          {rules.map((rule) => (
            <RuleCard
              key={rule.service_type}
              rule={rule}
              saving={saving === rule.service_type}
              onSave={(updated) => handleSave(rule.service_type, updated)}
            />
          ))}
        </div>
      )}

      {erpId && !loading && (
        <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
            {t('notifRules.manualTrigger')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('notifRules.manualTriggerDesc')}
          </p>
          <button
            disabled={triggerLoading}
            onClick={handleTriggerReminders}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Play size={15} />
            {triggerLoading ? t('common.loading') : t('notifRules.triggerBtn')}
          </button>
        </div>
      )}
    </div>
  )
}
